import { useEffect, useRef, useState } from 'react'
import { GoogleMap, MapType } from '@capacitor/google-maps'
import type { Airport } from '@/lib/airport-db'
import {
  bearing,
  flightProgress,
  greatCirclePoints,
  normalizeLon,
} from '@/lib/geo'
import {
  airportAnnotationElement,
  planeAnnotationElement,
} from '@/lib/flight-map-annotations'
import { useTransparentAppBackground } from '@/lib/use-transparent-background'

interface FlightMapGoogleProps {
  departure?: Airport
  arrival?: Airport
  departureDateTime?: Date | string
  arrivalDateTime?: Date | string
  /**
   * Single-point marker shown when no `departure`/`arrival` route is provided
   * (Home with no upcoming flight): centers on the device location.
   */
  location?: { latitude: number; longitude: number; label?: string }
  /** When false the map is a static backdrop (no pan/zoom/rotate). */
  interactive?: boolean
  /**
   * Insets (px) for fitting the route, so it stays clear of content floating
   * over the map (mobile bottom drawer).
   */
  edgePadding?: { top: number; left: number; bottom: number; right: number }
}

/** Bounds shape sent over the Capacitor bridge (plain data, not the class). */
interface PlainBounds {
  southwest: { lat: number; lng: number }
  northeast: { lat: number; lng: number }
}

/**
 * A framing target: a lat/lon box whose longitudes are UNWRAPPED (continuous,
 * minLon < maxLon, may extend past ±180) so antimeridian-crossing routes stay
 * a single well-ordered range.
 */
interface FrameBox {
  minLat: number
  maxLat: number
  minLon: number
  maxLon: number
}

// GoogleMap.create ids must be unique per live instance.
let instanceCounter = 0

/** A DOM annotation pinned to a coordinate, repositioned on camera changes. */
interface OverlayAnnotation {
  lat: number
  lon: number
  wrapper: HTMLElement
}

/** Web-Mercator y (radians) for a latitude, clamped clear of the poles. */
function mercY(latDeg: number): number {
  const φ = (Math.max(-89.9, Math.min(89.9, latDeg)) * Math.PI) / 180
  return Math.log(Math.tan(Math.PI / 4 + φ / 2))
}

/** Inverse of {@link mercY}: latitude (degrees) for a Web-Mercator y. */
function latFromMercY(merc: number): number {
  return ((2 * Math.atan(Math.exp(merc)) - Math.PI / 2) * 180) / Math.PI
}

/**
 * Web-Mercator projection of a coordinate to container pixels, derived from
 * the map's visible bounds. Only valid while the camera is unrotated and
 * untilted — which holds here: the camera is only ever positioned by
 * cameraForBox() below.
 */
function projectToPixels(
  lat: number,
  lon: number,
  bounds: PlainBounds,
  width: number,
  height: number,
): { x: number; y: number } {
  const west = bounds.southwest.lng
  let east = bounds.northeast.lng
  // Viewport straddling the antimeridian: unwrap east so west < east.
  if (east <= west) east += 360
  let lng = normalizeLon(lon)
  if (lng < west) lng += 360

  const north = mercY(bounds.northeast.lat)
  const south = mercY(bounds.southwest.lat)

  const x = ((lng - west) / (east - west)) * width
  const y = ((north - mercY(lat)) / (north - south)) * height
  return { x, y }
}

/**
 * Camera (center + zoom) that fits `box` inside the padded sub-rect of a
 * width×height viewport — the Google-Maps equivalent of MapKit's
 * `showItems(padding:)` / `setVisibleMapRect(edgePadding:)`.
 *
 * Computed in JS instead of the SDK's fitBounds + setPadding because the
 * native padding also shrinks what the SDK reports as the visible region,
 * which broke both the fit and the DOM-annotation projection (the projection
 * needs bounds covering the full element). With no native padding involved,
 * camera-idle bounds always span the whole element and everything stays
 * consistent.
 */
function cameraForBox(
  box: FrameBox,
  width: number,
  height: number,
  pad: { top: number; left: number; bottom: number; right: number },
): { lat: number; lng: number; zoom: number } {
  const paddedW = Math.max(40, width - pad.left - pad.right)
  const paddedH = Math.max(40, height - pad.top - pad.bottom)

  const lngSpan = Math.max(0.01, box.maxLon - box.minLon)
  const mercSpan = Math.max(0.0001, mercY(box.maxLat) - mercY(box.minLat))

  // World size (px) at which the box fits each padded dimension; the smaller
  // wins. zoom = log2(world / tileSize).
  const worldForWidth = (paddedW * 360) / lngSpan
  const worldForHeight = (paddedH * 2 * Math.PI) / mercSpan
  const zoom = Math.min(
    16,
    Math.max(0, Math.log2(Math.min(worldForWidth, worldForHeight) / 256)),
  )
  return centerAtZoom(box, width, height, pad, zoom)
}

/**
 * Camera centering `box` in the padded sub-rect at a FIXED zoom: the box's
 * Mercator center lands on the padded-region center (cropping symmetrically
 * when the box doesn't fit at this zoom).
 */
function centerAtZoom(
  box: FrameBox,
  width: number,
  height: number,
  pad: { top: number; left: number; bottom: number; right: number },
  zoom: number,
): { lat: number; lng: number; zoom: number } {
  const paddedW = Math.max(40, width - pad.left - pad.right)
  const paddedH = Math.max(40, height - pad.top - pad.bottom)
  const world = 256 * 2 ** zoom

  // Longitude: box center shifted by the padding asymmetry.
  const degPerPx = 360 / world
  const padOffsetX = (pad.left + paddedW / 2) - width / 2
  const centerLng = normalizeLon(
    (box.minLon + box.maxLon) / 2 - padOffsetX * degPerPx,
  )

  // Latitude: place the box's Mercator center at the padded-region center.
  // Screen y grows downward, Mercator y grows northward.
  const mercPerPx = (2 * Math.PI) / world
  const padOffsetY = (pad.top + paddedH / 2) - height / 2
  const centerMerc = (mercY(box.maxLat) + mercY(box.minLat)) / 2 + padOffsetY * mercPerPx

  return { lat: latFromMercY(centerMerc), lng: centerLng, zoom }
}

/** Effective zoom implied by the visible bounds of a `width`-px viewport. */
function zoomFromBounds(bounds: PlainBounds, width: number): number {
  const west = bounds.southwest.lng
  let east = bounds.northeast.lng
  if (east <= west) east += 360
  return Math.log2((width * 360) / (east - west) / 256)
}

/**
 * Android equivalent of the iOS `FlightMap` plugin: a native Google Map
 * (Maps SDK for Android via @capacitor/google-maps) rendered behind the
 * webview, showing the flight route or a single location pin.
 *
 * The airport/plane annotations are DOM elements floated over the map rather
 * than native markers: the plugin's Android marker icons only load from https
 * URLs or bundled assets, which can't render the dynamic ICAO labels. They are
 * projected to screen coordinates from the camera bounds, hidden while the
 * camera moves (bounds aren't streamed during gestures) and re-placed on idle.
 */
export function FlightMapGoogle({
  departure,
  arrival,
  departureDateTime,
  arrivalDateTime,
  location,
  interactive = true,
  edgePadding,
}: FlightMapGoogleProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  // The map lives in state (not a ref) so the content effect below re-runs
  // once the async native create completes.
  const [map, setMap] = useState<GoogleMap | null>(null)
  const annotationsRef = useRef<OverlayAnnotation[]>([])
  // Last framing target, so the resize observer can re-frame.
  const framedRef = useRef<FrameBox | null>(null)
  // Whether the camera-idle listener has placed the current annotations yet —
  // gates the fallback placement below.
  const placedRef = useRef(false)

  useTransparentAppBackground()

  const hasRoute = !!(departure && arrival)

  const placeAnnotations = (bounds: PlainBounds) => {
    const host = hostRef.current
    const overlay = overlayRef.current
    if (!host || !overlay) return
    const { width, height } = host.getBoundingClientRect()
    if (width === 0 || height === 0) return
    for (const a of annotationsRef.current) {
      const { x, y } = projectToPixels(a.lat, a.lon, bounds, width, height)
      a.wrapper.style.transform = `translate(${x}px, ${y}px)`
    }
    overlay.style.opacity = '1'
  }
  const placeAnnotationsRef = useRef(placeAnnotations)
  placeAnnotationsRef.current = placeAnnotations

  // Position the camera to frame framedRef within the current element size.
  const applyCamera = async (map: GoogleMap) => {
    const host = hostRef.current
    const framed = framedRef.current
    if (!host || !framed) return
    const { width, height } = host.getBoundingClientRect()
    if (width === 0 || height === 0) return
    const pad = edgePaddingRef.current ?? { top: 96, left: 80, bottom: 96, right: 80 }
    const camera = cameraForBox(framed, width, height, pad)
    await map.setCamera({
      coordinate: { lat: camera.lat, lng: camera.lng },
      zoom: camera.zoom,
      bearing: 0,
      angle: 0,
      animate: false,
    })

    // The SDK silently clamps to its device-dependent minimum zoom — a
    // Pacific-wide route can ask for a lower zoom than the map allows. The
    // center above was computed for the requested zoom, so a clamp leaves the
    // route far off-screen: read the zoom actually applied and, if it differs,
    // re-center for it (the route then crops symmetrically at the edges).
    const bounds = await map.getMapBounds()
    const actualZoom = zoomFromBounds(bounds, width)
    if (Math.abs(actualZoom - camera.zoom) > 0.05) {
      const corrected = centerAtZoom(framed, width, height, pad, actualZoom)
      await map.setCamera({
        coordinate: { lat: corrected.lat, lng: corrected.lng },
        zoom: actualZoom,
        bearing: 0,
        angle: 0,
        animate: false,
      })
    }
  }
  const edgePaddingRef = useRef(edgePadding)
  edgePaddingRef.current = edgePadding
  const applyCameraRef = useRef(applyCamera)
  applyCameraRef.current = applyCamera

  // Create the native map once (and tear it down on unmount).
  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    let cancelled = false
    let map: GoogleMap | null = null

    const el = document.createElement('capacitor-google-map')
    el.style.cssText = 'display:block;width:100%;height:100%;'
    host.prepend(el)

    GoogleMap.create({
      id: `flight-map-google-${++instanceCounter}`,
      element: el,
      apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '',
      config: { center: { lat: 30, lng: 0 }, zoom: 1 },
    })
      .then(async (m) => {
        if (cancelled) {
          m.destroy().catch(() => {})
          return
        }
        map = m
        await m.setMapType(MapType.Hybrid)
        // Reposition the DOM annotations whenever the camera settles; hide
        // them while it moves so they don't visibly drift off-coordinate.
        await m.setOnCameraIdleListener((data) => {
          placedRef.current = true
          placeAnnotationsRef.current(data.bounds)
        })
        await m.setOnCameraMoveStartedListener(() => {
          if (overlayRef.current) overlayRef.current.style.opacity = '0'
        })
        setMap(m)
      })
      .catch((e) => console.error('Google Map create failed', e))

    return () => {
      cancelled = true
      setMap(null)
      map?.destroy().catch(() => {})
      el.remove()
    }
  }, [])

  // Gate gestures on the `interactive` prop (backdrop maps are static).
  useEffect(() => {
    if (!map) return
    ;(interactive ? map.enableTouch() : map.disableTouch()).catch(() => {})
  }, [map, interactive])

  // Sync route/location content + framing whenever the inputs change.
  useEffect(() => {
    if (!map) return
    let cancelled = false
    let polylineIds: string[] = []
    const overlay = overlayRef.current

    const addAnnotation = (lat: number, lon: number, el: HTMLElement) => {
      const wrapper = document.createElement('div')
      wrapper.style.cssText = 'position:absolute;left:0;top:0;'
      wrapper.appendChild(el)
      overlay?.appendChild(wrapper)
      annotationsRef.current.push({ lat, lon, wrapper })
    }

    const run = async () => {
      if (hasRoute && departure && arrival) {
        // Native geodesic polylines only need the endpoints; the interpolated
        // points are still used for the plane position and camera bounds.
        const path = [
          { lat: departure.latitudeDeg, lng: departure.longitudeDeg },
          { lat: arrival.latitudeDeg, lng: arrival.longitudeDeg },
        ]
        // Solid double stroke matching the native MKMapView renderer
        // (FlightMapPlugin.swift): dark-blue outer line with a light-blue core.
        polylineIds = await map.addPolylines([
          {
            path,
            geodesic: true,
            strokeColor: '#215CD1',
            strokeOpacity: 0.95,
            strokeWeight: 3,
            zIndex: 1,
          },
          {
            path,
            geodesic: true,
            strokeColor: '#9EC7FF',
            strokeOpacity: 1,
            strokeWeight: 1.5,
            zIndex: 2,
          },
        ])
        if (cancelled) return

        const rawPoints = greatCirclePoints(
          departure.latitudeDeg,
          departure.longitudeDeg,
          arrival.latitudeDeg,
          arrival.longitudeDeg,
        )

        addAnnotation(
          departure.latitudeDeg,
          departure.longitudeDeg,
          airportAnnotationElement(departure.icaoCode),
        )
        addAnnotation(
          arrival.latitudeDeg,
          arrival.longitudeDeg,
          airportAnnotationElement(arrival.icaoCode),
        )

        // Plane glyph at the current flight-progress point along the geodesic,
        // headed toward the next point — mirrors the native plugin's
        // planePosition(on:progress:).
        const progress = flightProgress(departureDateTime, arrivalDateTime)
        const planeIdx = Math.min(
          rawPoints.length - 1,
          Math.max(0, Math.round((rawPoints.length - 1) * progress)),
        )
        const heading = bearing(
          rawPoints[Math.max(0, planeIdx - 1)],
          rawPoints[Math.min(rawPoints.length - 1, planeIdx + 1)],
        )
        addAnnotation(
          rawPoints[planeIdx].lat,
          normalizeLon(rawPoints[planeIdx].lon),
          planeAnnotationElement(heading),
        )

        // Frame the whole geodesic (it bulges poleward past the endpoints),
        // like showItems([dep, arr, outer]) does on MapKit. The interpolated
        // longitudes are already unwrapped/continuous, so min/max stay a
        // well-ordered range across the antimeridian.
        const lats = rawPoints.map((p) => p.lat)
        const lons = rawPoints.map((p) => p.lon)
        framedRef.current = {
          minLat: Math.min(...lats),
          maxLat: Math.max(...lats),
          minLon: Math.min(...lons),
          maxLon: Math.max(...lons),
        }
        await applyCameraRef.current(map)
      } else if (location) {
        // Same ring+pill annotation as the route endpoints — on iOS the
        // location pin goes through the shared AirportAnnotationView too.
        addAnnotation(
          location.latitude,
          location.longitude,
          airportAnnotationElement(location.label ?? ''),
        )
        // Frame a fixed span around the pin (the CoordinateSpan(8, 8) the
        // other renderers use) so the single annotation keeps a city-level
        // zoom.
        const span = 4
        framedRef.current = {
          minLat: Math.max(-89, location.latitude - span),
          maxLat: Math.min(89, location.latitude + span),
          minLon: location.longitude - span,
          maxLon: location.longitude + span,
        }
        await applyCameraRef.current(map)
      } else {
        framedRef.current = null
      }
    }

    placedRef.current = false
    run().catch((e) => console.error('Google Map content sync failed', e))

    // Fallback placement: camera-idle is the accurate signal, but it never
    // fires when the camera move is a no-op (already framed) — place from the
    // current bounds instead.
    const fallbackTimer = window.setTimeout(() => {
      if (placedRef.current) return
      map
        .getMapBounds()
        .then((bounds) => {
          if (!cancelled && !placedRef.current) {
            placeAnnotationsRef.current(bounds)
          }
        })
        .catch(() => {})
    }, 1200)

    return () => {
      cancelled = true
      window.clearTimeout(fallbackTimer)
      annotationsRef.current = []
      if (overlay) {
        overlay.replaceChildren()
        overlay.style.opacity = '0'
      }
      if (polylineIds.length) map.removePolylines(polylineIds).catch(() => {})
    }
  }, [
    map,
    hasRoute,
    departure?.icaoCode,
    departure?.latitudeDeg,
    departure?.longitudeDeg,
    arrival?.icaoCode,
    arrival?.latitudeDeg,
    arrival?.longitudeDeg,
    departureDateTime,
    arrivalDateTime,
    location?.latitude,
    location?.longitude,
    location?.label,
    edgePadding?.top,
    edgePadding?.left,
    edgePadding?.bottom,
    edgePadding?.right,
  ])

  // Re-frame when the container resizes (the plugin resizes the native view
  // itself, but the camera needs recomputing), debounced so drag-resizes
  // settle first. The camera move fires an idle, which re-places annotations.
  useEffect(() => {
    const host = hostRef.current
    if (!map || !host) return
    let timer: number | undefined
    const observer = new ResizeObserver(() => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        applyCameraRef.current(map).catch(() => {})
      }, 100)
    })
    observer.observe(host)
    return () => {
      window.clearTimeout(timer)
      observer.disconnect()
    }
  }, [map])

  return (
    <div ref={hostRef} className="relative w-full h-full">
      <div
        ref={overlayRef}
        className="absolute inset-0 overflow-hidden pointer-events-none transition-opacity duration-150"
        style={{ opacity: 0 }}
      />
    </div>
  )
}
